import { Link, useLocation } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import axios from "axios";
import { useEffect, useState } from "react";
import Loader from "../components/Loader";

const RenterDetail = () => {
  const location = useLocation();
  const qrResult = location.state?.qrResult || null;
  const clientType = location.state?.clientType || null; // 👈 Get clientType from navigation

  const [data, setData] = useState(null);

  //  Fetch client details by type and ID
  const handleData = async (id, type) => {
    try {
      const endpoint =
        type === "airClient"
          ? `https://bpohasil.ngrok.app/api/air-client/${id}`
          : `https://bpohasil.ngrok.app/api/client/${id}`;

      const res = await axios.get(endpoint);
      if (res.data.success) {
        setData(res.data.client);
      } else {
        alert(res.data.message || "Client not found");
        window.location.replace("/scanner");
      }
    } catch (error) {
      console.error("Error fetching client data:", error);
      alert("Failed to fetch client data. Please try again.");
    }
  };

  useEffect(() => {
    if (qrResult && clientType) handleData(qrResult, clientType);
  }, [qrResult, clientType]);

  //  Show loader while fetching data
  if (!data) {
    return (
      <MainLayout>
        <Loader />
      </MainLayout>
    );
  }

  //  Air Client Layout
  if (clientType === "airClient") {
    return (
      <MainLayout>
        <div className="flex flex-col items-center bg-gray-100 pt-6 pb-4 px-4">
          <h2 className="text-2xl font-semibold text-gray-800">Air Client Details</h2>
          <p className="text-gray-500 text-sm mt-1">
            Information for Client ID #{data.id}
          </p>
        </div>

        <div className="p-6 bg-white rounded-xl shadow-lg mx-auto max-w-3xl mt-4">
              <Link to={`/air-client-update/${data.id}`} className="flex justify-end">
        <button className="py-2 px-3 rounded-lg bg-yellow-600 text-white">Edit</button>
        </Link>
          <table className="w-full text-sm">
            <tbody>
              <Row label="Name" value={data.name} />
              <Row label="Account Number" value={data.accountNumber} />
              <Row label="Property Number" value={data.propertyNumber} />
              <Row label="Unit Number" value={data.unitNumber} />
              <Row label="Unit Address" value={data.unitAddress} />
              <Row label="Area Code" value={data.areaCode} />
              <Row label="Area Name" value={data.areaName} />
              <Row label="Collection Center" value={data.collectionCenter} />
              <Row label="Receipt Number" value={data.receiptNumber} />
              <Row label="Receipt Date" value={formatDate(data.receiptDate)} />
              <Row label="Payment Amount (RM)" value={formatNumber(data.paymentAmount)} />
              <Row label="Status" value={data.status} />
            </tbody>
          </table>
        </div>
      </MainLayout>
    );
  }

  //  Sewa Client Layout
  return (
    <MainLayout>
      <div className="flex flex-col items-center bg-gray-100 pt-6 pb-4 px-4">
        <h2 className="text-2xl font-semibold text-gray-800">Sewa Client Details</h2>
        <p className="text-gray-500 text-sm mt-1">
          Information for Client ID #{data.id}
        </p>
      </div>

      <div className="p-6 bg-white rounded-xl shadow-lg mx-auto max-w-3xl mt-4">
        <Link to={`/sewa-client-update/${data.id}`} className="flex justify-end">
        <button className="py-2 px-3 rounded-lg bg-yellow-600 text-white">Edit</button>
        </Link>
        <table className="w-full text-sm">
          <tbody>
            {/* 🔹 Personal & Property Info */}
            <Row label="Name" value={data.name} />
            <Row label="Identification No." value={data.identificationNumber} />
            <Row label="Status" value={data.status} />
            <Row label="Unit Number" value={data.unitNumber} />
            <Row label="Property Number" value={data.propertyNumber} />
            <Row label="Account Number" value={data.accountNumber} />
            <Row label="Rental Area" value={data.rentalArea} />
            <Row label="Rental Category" value={data.rentalCategory} />
            <Row label="Zone ID" value={data.zoneId} />
            <Row label="Area ID" value={data.areaId} />

            {/* 🔹 Financial Info */}
            <tr className="border-t border-gray-200">
              <td colSpan="2" className="py-3 px-4 text-gray-600 font-bold">
                Financial Information
              </td>
            </tr>
            <Row label="Rental Rate (RM)" value={formatNumber(data.rentalRate)} />
            <Row label="Discount (RM)" value={formatNumber(data.discount)} />
            <Row label="Rental Charge (RM)" value={formatNumber(data.rentalCharge)} />
            <Row label="Payment (RM)" value={formatNumber(data.payment)} />
            <Row
              label="Arrears (RM)"
              value={
                <span
                  className={
                    data.arrears > 0 ? "text-red-600 font-bold" : "text-green-600 font-semibold"
                  }
                >
                  {formatNumber(data.arrears)}
                </span>
              }
            />

            {/* 🔹 KPI / Additional Info */}
            <tr className="border-t border-gray-200">
              <td colSpan="2" className="py-3 px-4 text-gray-600 font-bold">
                Additional Information
              </td>
            </tr>
            <Row label="KPI Type" value={data.kpiType} />
            <Row label="KPI" value={data.kpi} />
            <Row label="Aid Recipient" value={data.aidRecipient} />
            <Row label="Salary Deduction" value={data.salaryDeduction} />

            {/* 🔹 Meta Info */}
            <tr className="border-t border-gray-200">
              <td colSpan="2" className="py-3 px-4 text-gray-600 font-bold">
                Record Information
              </td>
            </tr>
            <Row label="Created At" value={formatDate(data.createdAt)} />
            <Row label="Updated At" value={formatDate(data.updatedAt)} />
          </tbody>
        </table>
      </div>
    </MainLayout>
  );
};

//  Reusable Table Row
const Row = ({ label, value }) => (
  <tr className="border-b border-gray-100 hover:bg-gray-50 transition">
    <td className="py-2 px-4 font-medium text-gray-600 w-1/3">{label}</td>
    <td className="py-2 px-4 text-gray-800 font-semibold">{value || "-"}</td>
  </tr>
);

//  Format currency
const formatNumber = (num) => {
  if (num == null) return "-";
  return Number(num).toLocaleString("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

//  Format date
const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

export default RenterDetail;
